/**
 * ChainVault WP - Launchpad Page
 * IDO Projects + Token Creator Hub
 *
 * @package ChainVault
 * @since   1.0.0
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { ethers } from 'ethers';
import toast from 'react-hot-toast';
import './LaunchpadPage.css';

/* ── Participate Modal ── */
function ParticipateModal( { project, onClose } ) {
    const { address, walletType, builtInWallet, selectedNetwork } = useWallet();
    const [ amount, setAmount ] = useState( '' );
    const [ isSending, setIsSending ] = useState( false );
    const isSolana = (project.network || '').toLowerCase() === 'solana';
    const symbol   = isSolana ? 'SOL' : 'ETH';
    const explorerBase = isSolana ? 'https://solscan.io/tx/' : 'https://etherscan.io/tx/';

    const handleParticipate = async () => {
        if ( !amount || isNaN(amount) || parseFloat(amount) <= 0 ) { toast.error( 'Enter a valid amount.' ); return; }
        setIsSending( true );
        try {
            const settings = window.chainvaultSettings || {};
            const fees     = settings.launchpadFees || {};
            const feePct   = parseFloat( fees.idoParticipationFee || 2 ) / 100;
            const total    = parseFloat( amount );
            const fee      = total * feePct;
            const toProject= total - fee;

            if ( isSolana ) {
                const web3 = await import( '@solana/web3.js' );
                const connection = new web3.Connection( 'https://api.mainnet-beta.solana.com', 'confirmed' );
                let payer;
                if ( builtInWallet && builtInWallet.solana && builtInWallet.solana.secretKey ) {
                    payer = web3.Keypair.fromSecretKey( new Uint8Array( builtInWallet.solana.secretKey ) );
                } else { throw new Error( 'Built-in Solana wallet required.' ); }
                const projectKey = new web3.PublicKey( project.wallet || payer.publicKey.toBase58() );
                const lamports   = Math.floor( toProject * web3.LAMPORTS_PER_SOL );
                const tx         = new web3.Transaction().add( web3.SystemProgram.transfer({ fromPubkey: payer.publicKey, toPubkey: projectKey, lamports }) );
                const sig = await web3.sendAndConfirmTransaction( connection, tx, [payer] );
                toast.success( 'Participated! TX: ' + sig.slice(0,12) + '...' );
                onClose();
            } else {
                let signer;
                if ( walletType === 'metamask' && window.ethereum ) {
                    const provider = new ethers.BrowserProvider( window.ethereum );
                    signer = await provider.getSigner();
                } else if ( builtInWallet && builtInWallet.ethereum ) {
                    const rpc = 'https://ethereum-rpc.publicnode.com';
                    const provider = new ethers.JsonRpcProvider( rpc );
                    signer = new ethers.Wallet( builtInWallet.ethereum.privateKey, provider );
                } else { throw new Error( 'No wallet connected.' ); }
                const projectAddr = project.wallet || await signer.getAddress();
                const tx = await signer.sendTransaction({ to: projectAddr, value: ethers.parseEther( toProject.toString() ) });
                toast.success( 'Participated! TX: ' + tx.hash.slice(0,12) + '...' );
                onClose();
            }
        } catch(err) {
            toast.error( (err.message||'Failed').slice(0,100) );
        } finally { setIsSending(false); }
    };

    return (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:300,display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(6px)'}} onClick={onClose}>
            <div style={{background:'#111',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'20px',padding:'28px',width:'100%',maxWidth:'420px',margin:'16px'}} onClick={e=>e.stopPropagation()}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'20px'}}>
                    <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
                        <div style={{width:'40px',height:'40px',borderRadius:'50%',background:'#FC72FF',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:'700',color:'#fff',overflow:'hidden'}}>
                            { project.logo ? <img src={project.logo} alt={project.name} style={{width:'40px',height:'40px',objectFit:'cover'}} onError={e=>e.target.style.display='none'} /> : (project.symbol||'?').charAt(0) }
                        </div>
                        <div>
                            <div style={{fontWeight:'800',color:'#fff',fontSize:'16px'}}>{ project.name }</div>
                            <div style={{fontSize:'12px',color:'#888'}}>{ project.symbol }</div>
                        </div>
                    </div>
                    <button onClick={onClose} style={{background:'none',border:'none',color:'#888',fontSize:'20px',cursor:'pointer'}}>✕</button>
                </div>
                <div style={{marginBottom:'16px'}}>
                    <label style={{display:'block',fontSize:'12px',fontWeight:'600',color:'#aaa',marginBottom:'8px',textTransform:'uppercase',letterSpacing:'0.5px'}}>Amount ({ symbol })</label>
                    <input type="number" value={amount} onChange={e=>setAmount(e.target.value)} placeholder={"e.g. 0.5 "+symbol} style={{width:'100%',padding:'12px 14px',background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'10px',color:'#fff',fontSize:'16px',outline:'none',boxSizing:'border-box',fontFamily:'inherit'}} />
                </div>
                { project.min_buy && <div style={{fontSize:'12px',color:'#888',marginBottom:'4px'}}>Min: { project.min_buy } | Max: { project.max_buy || '—' }</div> }
                { project.price && <div style={{fontSize:'12px',color:'#888',marginBottom:'16px'}}>Token Price: { project.price }</div> }
                <button onClick={handleParticipate} disabled={isSending} style={{display:'block',width:'100%',padding:'14px',background:'linear-gradient(135deg,#7C63FF,#FC72FF)',border:'none',borderRadius:'12px',color:'#fff',fontWeight:'700',fontSize:'15px',cursor:'pointer',fontFamily:'inherit',opacity:isSending?0.6:1}}>
                    { isSending ? 'Processing...' : '🚀 Participate Now' }
                </button>
                <div style={{fontSize:'11px',color:'#555',textAlign:'center',marginTop:'10px'}}>2% platform fee applies</div>
            </div>
        </div>
    );
}

/* ── IDO Card ── */
function IDOCard( { project, onParticipate, onViewDetails } ) {
    const getStatusClass = s => s === 'live' ? 'live' : s === 'ended' ? 'ended' : 'upcoming';
    const getStatusLabel = s => s === 'live' ? '● Live' : s === 'ended' ? 'Ended' : '⏳ Upcoming';
    const progress = Math.min( 100, parseInt( project.progress ) || 0 );
    const networkColors = { solana: '#9945FF', ethereum: '#627EEA', bsc: '#F0B90B', polygon: '#8247E5', base: '#0052FF' };
    const netColor = networkColors[(project.network||'ethereum').toLowerCase()] || '#7C63FF';

    return (
        <div className="lp-ido-card">
            <div className="lp-ido-card-banner" style={{background:'linear-gradient(135deg,'+netColor+'22,#0d0d0d)'}}>
                <div className="lp-ido-card-logo">
                    { project.logo ? <img src={project.logo} alt={project.name} onError={e=>e.target.style.display='none'} /> : (project.symbol||'?').charAt(0) }
                </div>
                <span className={"lp-ido-card-status "+getStatusClass(project.status)}>{ getStatusLabel(project.status) }</span>
            </div>
            <div className="lp-ido-card-body">
                <div className="lp-ido-card-name">{ project.name }</div>
                <div className="lp-ido-card-symbol">{ project.symbol }</div>
                <div className="lp-ido-card-network" style={{borderColor:netColor+'44',color:netColor}}>{ (project.network||'Ethereum') }</div>
                { project.description && <div className="lp-ido-card-desc">{ project.description }</div> }
                <div className="lp-ido-card-stats">
                    <div className="lp-ido-stat"><div className="lp-ido-stat-label">Token Price</div><div className="lp-ido-stat-value">{ project.price || 'TBA' }</div></div>
                    <div className="lp-ido-stat"><div className="lp-ido-stat-label">Target Raise</div><div className="lp-ido-stat-value">{ project.raise || 'TBA' }</div></div>
                </div>
                <div className="lp-ido-progress-wrap">
                    <div className="lp-ido-progress-top">
                        <span>Progress</span>
                        <span>{ progress }%</span>
                    </div>
                    <div className="lp-ido-progress-bar">
                        <div className="lp-ido-progress-fill" style={{width:progress+'%'}} />
                    </div>
                </div>
                <button
                    className="lp-ido-card-btn"
                    onClick={ () => onViewDetails( project ) }
                >
                    { 'View Project →' }
                </button>
            </div>
        </div>
    );
}

/* ── Main Launchpad Page ── */
function LaunchpadPage() {
    const navigate = useNavigate();
    const { address, setShowOnboarding } = useWallet();
    const [ selectedProject, setSelectedProject ] = useState( null );
    const settings  = window.chainvaultSettings || {};
    const projects  = Array.isArray( settings.idoProjects ) ? settings.idoProjects : [];

    const CREATORS = [
        { icon: '⚡', title: 'ERC20 Token', chains: 'Ethereum · BSC · Polygon · Base', route: '/launchpad/create-erc20', color: '#627EEA' },
        { icon: '◎',  title: 'SPL Token',   chains: 'Solana',                          route: '/launchpad/create-spl',   color: '#9945FF' },
        { icon: '🚀', title: 'Airdrop Sender', chains: 'ETH · BSC · Polygon · Base · SOL',  route: '/launchpad/multisender',  color: '#7C63FF' },
    ];

    return (
        <div className="lp-page">
            <div className="lp-hero">
                <h1>Token Launchpad</h1>
                <p>Launch your token, send airdrops or participate in presales — no coding required</p>
                <div className="lp-creator-cards">
                    { CREATORS.map( c => (
                        <div key={c.route} className="lp-creator-card" onClick={()=>navigate(c.route)} style={{'--card-color':c.color}}>
                            <div className="lp-creator-card-icon">{ c.icon }</div>
                            <div className="lp-creator-card-title">{ c.title }{ c.route !== '/launchpad/multisender' ? ' Creator' : '' }</div>
                            <div className="lp-creator-card-chains">{ c.chains }</div>
                        </div>
                    ) ) }
                </div>
            </div>

            <div className="lp-ido-section-title">
                <h2>🚀 IDO Projects</h2>
                <span className="lp-ido-count">{ projects.length } project{ projects.length !== 1 ? 's' : '' }</span>
            </div>

            { projects.length === 0 ? (
                <div style={{textAlign:'center',padding:'60px 20px',background:'rgba(255,255,255,0.02)',borderRadius:'16px',border:'1px solid rgba(255,255,255,0.06)'}}>
                    <div style={{fontSize:'48px',marginBottom:'16px'}}>🚀</div>
                    <h3 style={{color:'#fff',marginBottom:'8px'}}>No IDO Projects Yet</h3>
                    <p style={{color:'#aaa',fontSize:'14px',marginBottom:'24px'}}>The site admin has not added any presale projects yet. Check back soon.</p>
                    <div style={{background:'rgba(124,99,255,0.08)',border:'1px solid rgba(124,99,255,0.2)',borderRadius:'12px',padding:'16px 20px',maxWidth:'400px',margin:'0 auto'}}>
                        <div style={{fontSize:'13px',fontWeight:'700',color:'#7C63FF',marginBottom:'6px'}}>Want to list your project?</div>
                        <div style={{fontSize:'12px',color:'#888'}}>Contact us to list your IDO. Listing fees start from $500.</div>
                    </div>
                </div>
            ) : (
                <div className="lp-ido-grid">
                    { projects.map( (p,i) => (
                        <IDOCard key={i} project={p}
                            onViewDetails={ proj => { const slug = (proj.name||'').toLowerCase().replace(/\s+/g,'-'); navigate('/launchpad/ido/'+slug); } }
                            onParticipate={ proj => { if (!address) { setShowOnboarding(true); return; } setSelectedProject(proj); } }
                        />
                    )) }
                </div>
            ) }

            { selectedProject && <ParticipateModal project={selectedProject} onClose={()=>setSelectedProject(null)} /> }
        </div>
    );
}

export default LaunchpadPage;
